import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { BrCard, BrButton, BrSelect } from '@govbr-ds/react-components'
import { executeTestCase } from '../services/api'
import { TestCaseWithId, getExecutions, ExecutionWithId } from '../services/firestore-proxy'
import { DynamicUIRenderer } from '../components/DynamicUIRenderer'
import { PERSONA_LABELS, TASK_LABELS, PHASE_LABELS } from '../constants/labels'

// Real TST test cases (fetched from DataJud)
import realTestCases from '../../data/real_tst_test_cases.json'

/**
 * Maps profile_id to accessibility_needs for CSS styling.
 * This allows generic CSS selectors like [data-accessibility*="low_vision"]
 * to work for any persona with those needs.
 */
function getAccessibilityNeedsFromProfile(profileId: string): string {
  const accessibilityMap: Record<string, string> = {
    'john_smith': 'low_vision',
    'dra_emily': 'low_vision_and_photophobia',
    'olivia': 'none',
    'dr_charles': 'none',
  };
  return accessibilityMap[profileId] || 'none';
}


export function TestCasesPage() {
  const [testCases, setTestCases] = useState<TestCaseWithId[]>([])
  const [executions, setExecutions] = useState<ExecutionWithId[]>([])
  const [loading, setLoading] = useState(true)
  const [executing, setExecuting] = useState<Set<string>>(new Set())
  const [previewExecution, setPreviewExecution] = useState<ExecutionWithId | null>(null)
  const [filter, setFilter] = useState({
    persona: '',
    taskType: '',
    phase: '',
  })
  const { t } = useTranslation()

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    setLoading(true)
    try {
      // Use real TST test cases from DataJud
      const realCases = (realTestCases.test_cases as unknown as TestCaseWithId[]).map((tc) => ({
        ...tc,
        id: tc.case_id,
      }))
      setTestCases(realCases)

      // Load executions
      const result = await getExecutions({ orderBy: 'created_at_desc', limit: 100 })
      if (result.success && result.data) {
        setExecutions(result.data)
      }
    } catch (error) {
      console.error('Error loading data:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleExecute = async (testCase: TestCaseWithId) => {
    const caseId = testCase.case_id
    setExecuting(prev => new Set(prev).add(caseId))
    try {
      const result = await executeTestCase(testCase)
      if (result.success) {
        console.log('Execution result:', result.data)
        // Reload executions to show the new one
        const execResult = await getExecutions({ orderBy: 'created_at_desc', limit: 100 })
        if (execResult.success && execResult.data) {
          setExecutions(execResult.data)
        }
      } else {
        console.error('Execution failed:', result.error)
      }
    } catch (error) {
      console.error('Error executing test case:', error)
    } finally {
      setExecuting(prev => {
        const next = new Set(prev)
        next.delete(caseId)
        return next
      })
    }
  }

  // Find execution for a test case
  const getExecutionForCase = (caseId: string): ExecutionWithId | undefined => {
    return executions.find(e => e.case_id === caseId)
  }

  const filteredCases = testCases.filter((tc) => {
    if (filter.persona && tc.profile.profile_id !== filter.persona) return false
    if (filter.taskType && tc.task_type !== filter.taskType) return false
    if (filter.phase && tc.process_phase !== filter.phase) return false
    return true
  })

  const uniquePersonas = [...new Set(testCases.map((tc) => tc.profile.profile_id))]
  const uniqueTasks = [...new Set(testCases.map((tc) => tc.task_type))]
  const uniquePhases = [...new Set(testCases.map((tc) => tc.process_phase))]

  const personaOptions = [{ value: '', label: t('common.all', 'All') }, ...uniquePersonas.map((p) => ({
    value: p,
    label: PERSONA_LABELS[p]?.name || p
  }))]

  const taskOptions = [{ value: '', label: t('common.all') }, ...uniqueTasks.map((t) => ({
    value: t,
    label: TASK_LABELS[t] || t
  }))]

  const phaseOptions = [{ value: '', label: t('common.all') }, ...uniquePhases.map((p) => ({
    value: p,
    label: PHASE_LABELS[p] || p
  }))]

  if (loading) {
    return (
      <div className="d-flex align-items-center justify-content-center min-h-[400px]">
        <div className="loading mr-1"></div>
        <span className="text-gray-60">Carregando...</span>
      </div>
    )
  }

  return (
    <div className="d-flex flex-column gap-6">
      <h1 className="text-2xl font-bold text-gray-80">{t('testCases.title')}</h1>

      {/* Preview Modal */}
      {previewExecution && (
        <div className="modal-overlay" style={{ zIndex: 9999 }}>
          <div className="modal-content d-flex flex-column" style={{ maxHeight: '90vh' }}>
            <div className="p-4 border-b bg-primary-default text-pure-0">
              <h2 className="font-bold m-0">Interface Gerada</h2>
              <p className="text-sm opacity-90 m-0">
                {previewExecution.case_id} | {previewExecution.profile_id || 'N/A'} | {previewExecution.task_type || 'N/A'}
              </p>
            </div>
            <div
              className="flex-1 overflow-auto p-4 bg-gray-2"
              data-accessibility={getAccessibilityNeedsFromProfile(previewExecution.profile_id || '')}
              data-persona={previewExecution.profile_id}
              style={{
                backgroundColor: getAccessibilityNeedsFromProfile(previewExecution.profile_id || '').includes('photophobia') ? '#1a1a1a' : undefined
              }}
            >
              {previewExecution.ui_spec ? (
                <DynamicUIRenderer uiSpec={
                  typeof previewExecution.ui_spec === 'string'
                    ? JSON.parse(previewExecution.ui_spec)
                    : previewExecution.ui_spec
                } />
              ) : (
                <p className="text-center text-gray-60 py-8">
                  Nenhuma interface gerada para esta execução
                </p>
              )}
            </div>
            <div className="p-4 border-t d-flex justify-content-between align-items-center bg-pure-0">
              <div className="text-sm text-gray-60">
                {previewExecution.status === 'success' ? (
                  <span className="text-success">✓ Sucesso</span>
                ) : (
                  <span className="text-danger">✗ Falha</span>
                )}
                {previewExecution.metrics && previewExecution.metrics.tads != null && (
                  <span className="ml-4">
                    TADS: {(previewExecution.metrics.tads * 100).toFixed(0)}%
                  </span>
                )}
              </div>
              <div className="d-flex gap-3">
                <BrButton
                  secondary
                  onClick={() => setPreviewExecution(null)}
                >
                  Fechar
                </BrButton>
                <button
                  type="button"
                  className="br-button primary"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    window.open(`/preview/${previewExecution.execution_id}`, '_blank');
                  }}
                >
                  Abrir em nova aba ↗
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Data Source Info */}
      <BrCard className="bg-info" style={{ backgroundColor: 'rgba(21, 91, 203, 0.1)' }}>
        <div className="d-flex align-items-center justify-content-between">
          <div>
            <h3 className="font-semibold text-info">🏛️ {t('testCases.dataSource')}</h3>
            <p className="text-sm text-gray-60 mt-1">
              {t('testCases.dataSourceDesc')} ({realTestCases.test_cases.length} casos) - Gerado em {realTestCases.generated_at.split('T')[0]}
            </p>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-primary-default">{executions.length}</p>
            <p className="text-xs text-gray-60">{t('testCases.generatedInterfaces')}</p>
          </div>
        </div>
      </BrCard>



      {/* Filters */}
      <BrCard>
        <div className="row">
          <div className="col-12 col-md-4 mb-3 mb-md-0">
            <BrSelect
              id="filter-persona"
              label="Persona"
              options={personaOptions}
              value={filter.persona}
              onChange={(value) => setFilter({ ...filter, persona: value as string })}
            />
          </div>
          <div className="col-12 col-md-4 mb-3 mb-md-0">
            <BrSelect
              id="filter-task-type"
              label={t('testCases.taskType')}
              options={taskOptions}
              value={filter.taskType}
              onChange={(value) => setFilter({ ...filter, taskType: value as string })}
            />
          </div>
          <div className="col-12 col-md-4">
            <BrSelect
              id="filter-phase"
              label={t('testCases.phaseFilter')}
              options={phaseOptions}
              value={filter.phase}
              onChange={(value) => setFilter({ ...filter, phase: value as string })}
            />
          </div>
        </div>
      </BrCard>

      {/* Test Cases List */}
      <div className="d-flex flex-column gap-4">
        {filteredCases.length === 0 ? (
          <BrCard>
            <p className="text-center text-gray-60">
              {t('testCases.noResults')}
            </p>
          </BrCard>
        ) : (
          filteredCases.map((testCase) => {
            const execution = getExecutionForCase(testCase.case_id)
            return (
              <BrCard key={testCase.case_id}>
                <div className="d-flex flex-column md:flex-row align-items-start justify-content-between gap-3">
                  <div className="flex-1">
                    <div className="d-flex flex-wrap align-items-center gap-2">
                      <h3 className="font-semibold text-gray-80">
                        {testCase.case_id}
                      </h3>
                      {execution && (
                        <span className={`text-xs px-2 py-0.5 rounded-full ${execution.status === 'success'
                          ? 'bg-success text-success'
                          : 'bg-danger text-danger'
                          }`}>
                          {execution.status === 'success' ? `✓ ${t('testCases.executed')}` : `✗ ${t('dashboard.failed')}`}
                        </span>
                      )}
                    </div>
                    <div className="mt-2 d-flex flex-column gap-2 text-sm">
                      <div>
                        <span className="text-gray-60">{t('testCases.filters.persona')}: </span>
                        <span className="font-medium">
                          {PERSONA_LABELS[testCase.profile.profile_id]?.name || testCase.profile.profile_id}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-60">{t('testCases.task')}: </span>
                        <span className="font-medium">{TASK_LABELS[testCase.task_type] || testCase.task_type}</span>
                      </div>
                      <div>
                        <span className="text-gray-60">{t('testCases.phase')}: </span>
                        <span className="font-medium">{PHASE_LABELS[testCase.process_phase] || testCase.process_phase}</span>
                      </div>
                    </div>
                    <div className="mt-2 text-sm">
                      <span className="text-gray-60">
                        {t('testCases.accessibility')}:{' '}
                      </span>
                      <span className="font-medium">
                        {testCase.profile.accessibility_needs === 'none'
                          ? t('testCases.none')
                          : testCase.profile.accessibility_needs}
                      </span>
                    </div>
                    {execution && execution.metrics && (
                      <div className="mt-2 text-xs text-gray-60">
                        TADS: {execution.metrics.tads != null ? (execution.metrics.tads * 100).toFixed(0) : 'N/A'}% |
                        Flesch: {execution.metrics.flesch_index != null ? execution.metrics.flesch_index.toFixed(0) : 'N/A'} |
                        Componentes: {execution.metrics.component_count ?? 'N/A'}
                      </div>
                    )}
                  </div>
                  <div className="d-flex flex-column md:flex-row gap-2 w-full md:w-auto">
                    {execution && execution.ui_spec && (
                      <BrButton
                        primary
                        onClick={() => setPreviewExecution(execution)}
                        className="w-full md:w-auto"
                      >
                        👁️ {t('testCases.viewInterface')}
                      </BrButton>
                    )}
                    <BrButton
                      secondary
                      onClick={() => handleExecute(testCase)}
                      loading={executing.has(testCase.case_id)}
                      className="w-full md:w-auto"
                    >
                      {execution ? `🔄 ${t('testCases.reExecute')}` : t('testCases.execute')}
                    </BrButton>
                  </div>
                </div>
              </BrCard>
            )
          })
        )}
      </div>

      <p className="text-sm text-gray-60 text-center">
        {t('common.showing', 'Showing')} {filteredCases.length} {t('common.of')} {testCases.length} {t('testCases.title').toLowerCase()}
      </p>
    </div>
  )
}
